import type { JTDDataType } from 'ajv/dist/jtd';

import { ARRAY_CONFIGURATION_TYPE } from './data-source-dynamic-form.constants';
import type {
  AVAILABILITY_TYPES,
  BOOLEAN_CONFIGURATION_TYPE,
  CONFIGURATION_TYPES,
  INTEGER_CONFIGURATION_TYPE,
  PASSWORD_CONFIGURATION_TYPE,
  PASSWORD_MULTILINE_CONFIGURATION_TYPE,
  TEXT_CONFIGURATION_TYPE,
  USERNAME_CONFIGURATION_TYPE,
} from './data-source-dynamic-form.constants';
import type {
  arrayFieldDefinitionSchema,
  dataSourceDefinitionSchema,
  primitiveFieldDefinitionSchema,
  sectionDefinitionSchema,
} from './data-source-dynamic-form.schemas';

export type ConfigurationType = (typeof CONFIGURATION_TYPES)[number];

export type AvailabilityType = (typeof AVAILABILITY_TYPES)[number];

export type PrimitiveFieldDefinition = Omit<JTDDataType<typeof primitiveFieldDefinitionSchema>, 'default'> & {
  default?: FormFieldValue;
};

export type ArrayFieldDefinition = Omit<JTDDataType<typeof arrayFieldDefinitionSchema>, 'default' | 'fields'> & {
  fields: PrimitiveFieldDefinition[];
};

export const isArrayFieldDefinition = (field: FieldDefinition): field is ArrayFieldDefinition => {
  return field.type === ARRAY_CONFIGURATION_TYPE;
};

export const isPrimitiveFieldDefinition = (field: FieldDefinition): field is PrimitiveFieldDefinition => {
  return field.type !== ARRAY_CONFIGURATION_TYPE;
};

export type FieldDefinition = PrimitiveFieldDefinition | ArrayFieldDefinition;

export type SectionDefinition = Omit<JTDDataType<typeof sectionDefinitionSchema>, 'fields'> & {
  fields: FieldDefinition[];
};

export type DataSourceDefinition = Omit<JTDDataType<typeof dataSourceDefinitionSchema>, 'sections'> & {
  sections: SectionDefinition[];
};

export type PrimitiveFormFieldValue = string | number | boolean;
export type PrimitiveError = Record<string, string | undefined | null>;
export type DataSourceValidationErrors = Record<string, string | undefined | null | PrimitiveError[]>;

type FormLabel = {
  label: string;
  description: string;
  required: boolean;
};

export type CommonFieldProps = {
  name: string;
  type: ConfigurationType;
};

export type CommonPrimitiveField = CommonFieldProps & {
  value: PrimitiveFormFieldValue;
};
export type NumberField = CommonPrimitiveField & {
  type: typeof INTEGER_CONFIGURATION_TYPE;
  value: number;
};
export type NumberFormField = NumberField & FormLabel;
export type TextField = CommonPrimitiveField & {
  type:
    | typeof TEXT_CONFIGURATION_TYPE
    | typeof USERNAME_CONFIGURATION_TYPE
    | typeof PASSWORD_CONFIGURATION_TYPE
    | typeof PASSWORD_MULTILINE_CONFIGURATION_TYPE;
  value: string;
};
export type TextFormField = TextField & FormLabel;
export type BooleanField = CommonPrimitiveField & {
  type: typeof BOOLEAN_CONFIGURATION_TYPE;
  value: boolean;
};
export type BooleanFormField = BooleanField & FormLabel;

export type ArrayFormFieldValueElement = Record<string, PrimitiveFormField>;
export type ArrayFormFieldValue = ArrayFormFieldValueElement[];
export type FormFieldValue = PrimitiveFormFieldValue | ArrayFormFieldValue;
export type PrimitiveFieldValue = PrimitiveFormFieldValue;
export type FieldValue = PrimitiveFieldValue | ArrayFieldValue;

export const isArrayFormFieldValue = (value: FormFieldValue | undefined): value is ArrayFormFieldValue => {
  return value !== undefined && Array.isArray(value);
};

export type ArrayFieldValueElement = Record<string, PrimitiveField>;
export type ArrayFieldValue = ArrayFieldValueElement[];
export type ArrayField = CommonFieldProps & {
  type: typeof ARRAY_CONFIGURATION_TYPE;
  value: ArrayFieldValue;
};

export type PrimitiveField = NumberField | TextField | BooleanField;
export type Field = PrimitiveField | ArrayField;

export type PrimitiveFormField = NumberFormField | TextFormField | BooleanFormField;

export type ArrayFormField = Omit<ArrayFieldDefinition, 'fields'> &
  FormLabel &
  CommonFieldProps & {
    type: typeof ARRAY_CONFIGURATION_TYPE;
    value: ArrayFormFieldValueElement[];
  };

export const isArrayFormField = (field: FormField | Field | undefined): field is ArrayFormField => {
  return field !== undefined && 'value' in field && 'description' in field && Array.isArray(field.value);
};

export const isArrayField = (field: Field | undefined): field is ArrayField => {
  return field !== undefined && 'value' in field && Array.isArray(field.value);
};

export const isArrayFieldValue = (value: FieldValue | undefined): value is ArrayFieldValue => {
  return value !== undefined && Array.isArray(value);
};

export const isPrimitiveFormField = (field: FormField | Field | undefined): field is PrimitiveFormField => {
  return field !== undefined && 'label' in field && !isArrayFormField(field);
};

export const isPrimitiveField = (field: Field | undefined): field is PrimitiveField => {
  return field !== undefined && !isArrayField(field);
};

export type FormField = PrimitiveFormField | ArrayFormField;

// Full config contains everything to know how to render the form
export type DataSourceFullConfig = {
  name: string;
  type: string;
  fields: FormField[];
};

// Config doesn't contain labels and other UI related information
export type DataSourceConfig = {
  name: string;
  type: string;
  fields: Field[];
};

export type ApiDataSourceDeclarations = {
  version: string;
  sources: DataSourceDefinition[];
};

export type ApiPrimitiveField = { type: ConfigurationType; value: PrimitiveFieldValue };
export type ApiArrayFieldValueElement = Record<string, ApiPrimitiveField>;
export type ApiArrayField = { type: 'array'; value: ApiArrayFieldValueElement[] };
export const isApiArrayField = (field: ApiDynamicField): field is ApiArrayField => {
  return field.type === 'array' && Array.isArray(field.value);
};
export const isApiPrimitiveField = (field: ApiDynamicField): field is ApiPrimitiveField => {
  return field.type !== 'array';
};

export type ApiDynamicField = ApiPrimitiveField | ApiArrayField;

export type ApiDynamicDataSourceParams = {
  type: string;
  parameters: Record<string, ApiDynamicField>;
};

export type ApiSavedDataSourceConfig = {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  hasPassword: boolean;
} & ApiDynamicDataSourceParams;

export type SavedDataSourceConfig = {
  id: string;
  createdAt: string;
  updatedAt: string;
  hasPassword: boolean;
} & DataSourceConfig;
